import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, Clock, Calendar, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { showToast } from '../utils/toast';
import { formatDateEST } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientOnboarding() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState({ completed: 0, total: 0, progress: 0 });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API}/api/client/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const clientTasks = data.onboarding_tasks || [];
        setTasks(clientTasks);
        
        const completed = clientTasks.filter(t => t.is_completed).length;
        const total = clientTasks.length;
        const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
        
        setStats({ completed, total, progress });
      } else if (response.status === 401) {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    // Only allow clients to complete client-responsible tasks
    const task = tasks.find(t => t.task_id === taskId);
    if (task.responsible_party === 'staff') {
      showToast({
        title: 'Cannot modify',
        message: 'This task is managed by your Project Manager',
        type: 'error'
      });
      return;
    }

    setUpdating(taskId);

    try {
      const response = await fetch(`${API}/api/client/onboarding/task/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          is_completed: !currentStatus
        })
      });

      if (response.ok) {
        await fetchTasks();
        showToast({
          title: currentStatus ? 'Task unmarked' : 'Task completed!',
          message: currentStatus ? 'Task marked as incomplete' : 'Great progress!',
          type: 'success'
        });
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      showToast({
        title: 'Update failed',
        message: 'Could not update task status',
        type: 'error'
      });
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    return formatDateEST(dateString);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative border-b border-white/10 bg-black/20 backdrop-blur-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/client-dashboard')}
              className="text-white hover:bg-white/10 touch-manipulation"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">Onboarding Tasks</h1>
              <p className="text-blue-200 text-sm">Track your progress to launch</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Progress Card */}
        <div className="mb-6 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-3xl p-6 sm:p-8 shadow-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-slate-900/80 text-sm font-medium mb-1">Overall Progress</p>
              <h2 className="text-4xl font-bold text-slate-900">
                {stats.progress}%
              </h2>
              <p className="text-slate-900/70 text-sm mt-1">
                {stats.completed} of {stats.total} tasks complete
              </p>
            </div>
            <div className="w-32 h-32 relative">
              <svg className="transform -rotate-90 w-32 h-32">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  className="text-slate-900/20"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 56}`}
                  strokeDashoffset={`${2 * Math.PI * 56 * (1 - stats.progress / 100)}`}
                  className="text-slate-900 transition-all duration-1000"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <CheckCircle2 className="h-12 w-12 text-slate-900" />
              </div>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="space-y-3">
          {tasks.map((task) => (
            <div
              key={task.task_id}
              className={`bg-white/10 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border transition-all duration-300 ${
                task.is_completed
                  ? 'border-green-400/30 bg-green-400/5'
                  : 'border-white/20 hover:border-white/40'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <button
                  onClick={() => handleToggleTask(task.task_id, task.is_completed)}
                  disabled={updating === task.task_id || task.responsible_party === 'staff'}
                  className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all touch-manipulation ${
                    task.is_completed
                      ? 'bg-green-400 border-green-400'
                      : task.responsible_party === 'staff'
                      ? 'border-gray-400 bg-gray-400/20 cursor-not-allowed'
                      : 'border-yellow-400 hover:bg-yellow-400/20'
                  } ${updating === task.task_id ? 'animate-pulse' : ''}`}
                >
                  {updating === task.task_id ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : task.is_completed ? (
                    <CheckCircle2 className="h-5 w-5 text-white" />
                  ) : (
                    <Circle className="h-5 w-5 text-yellow-400" />
                  )}
                </button>

                {/* Task Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h3 className={`font-semibold ${
                      task.is_completed ? 'text-white/70 line-through' : 'text-white'
                    }`}>
                      {task.title}
                    </h3>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        task.responsible_party === 'client'
                          ? 'bg-yellow-400/20 text-yellow-300'
                          : 'bg-blue-400/20 text-blue-300'
                      }`}>
                        {task.responsible_party === 'client' ? 'Your Task' : 'Staff Task'}
                      </span>
                    </div>
                  </div>
                  
                  <p className={`text-sm mb-3 ${
                    task.is_completed ? 'text-blue-200/60' : 'text-blue-200'
                  }`}>
                    {task.description}
                  </p>

                  {/* Task Meta */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-blue-300">
                    {task.due_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>Due: {formatDate(task.due_date)}</span>
                      </div>
                    )}
                    
                    {task.completed_at && (
                      <div className="flex items-center gap-1 text-green-300">
                        <CheckCircle2 className="h-3 w-3" />
                        <span>Completed {formatDate(task.completed_at)}</span>
                      </div>
                    )}
                    
                    {task.notes && task.notes.length > 0 && (
                      <div className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        <span>{task.notes.length} note{task.notes.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {task.notes && task.notes.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {task.notes.map((note, idx) => (
                        <div key={idx} className="bg-white/5 rounded-lg p-2 border border-white/10">
                          <p className="text-sm text-white">{note.text}</p>
                          <p className="text-xs text-blue-300/60 mt-1">
                            {note.added_by} • {formatDate(note.added_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {tasks.length === 0 && (
          <div className="text-center py-12 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20">
            <Clock className="h-12 w-12 text-blue-300 mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">No tasks yet</p>
            <p className="text-blue-200 text-sm">Your Project Manager will add tasks soon</p>
          </div>
        )}
      </main>

      {/* Animation styles */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
